import type { CustomField } from '@libs/domain';
import type { MstSearchDialogProps, MstSearchProps } from '@libs/templates/mst';
import { MstSearchDialogTmpl, genMstColumns, mstListSearchFieldConfig } from '@libs/templates/mst';
import type { Characters, Item, PageProps } from '@ps-smart-cp/ppfw-client';
import { MasterSearch, MasterSearchField, withRerender } from '@ps-smart-cp/ppfw-client';
import { useCustomFieldBasicSettings } from '../hooks';
import { CustomMstListSearch } from '../models';
import type { CustomMstRegistration } from '../models';
import { fetchMst, listRequest } from '../repositories';
import { createCustomMstItemName } from '../services';

function InnerComponent({
  customFieldKey,
  ...restProps
}: MstSearchDialogProps<CustomMstRegistration> & PageProps & Pick<CustomField['schema'], 'customFieldKey'>) {
  const customFMstSettings = useCustomFieldBasicSettings();

  return (
    <MstSearchDialogTmpl
      mstName={createCustomMstItemName(customFieldKey, customFMstSettings)}
      columns={genMstColumns<CustomMstRegistration>()}
      items={mstListSearchFieldConfig}
      listRequestFunction={listRequest(customFieldKey)()}
      initMst={CustomMstListSearch.init}
      {...restProps}
    />
  );
}

const Dialog = withRerender(InnerComponent, 'CustomMstDialog');

function customMstSearchField<K extends 'customFieldValue'>(_: K) {
  return function CustomMasterSearchField<
    T extends Pick<CustomField['schema'], K>,
    I extends Record<K, Item<Characters>>,
  >({
    customFieldKey,
    ...restProps
  }: MstSearchProps<T, K, I> & Pick<CustomField['schema'], 'customFieldKey'>) {
    return (
      <MasterSearchField getMaster={async (code) => fetchMst(customFieldKey)()({ code })} {...restProps}>
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            customFieldKey={customFieldKey}
            getMaster={getMst}
          />
        )}
      </MasterSearchField>
    );
  };
}

function customMstSearch<K extends 'customFieldValue'>(_: K) {
  return function GridCustomMasterSearchField<
    T extends Pick<CustomField['schema'], K>,
    I extends Record<K, Item<Characters>>,
  >({
    customFieldKey,
    ...restProps
  }: MstSearchProps<T, K, I> & Pick<CustomField['schema'], 'customFieldKey'>) {
    return (
      <MasterSearch
        getMaster={async (code) => fetchMst(customFieldKey)()({ code })}
        itemAttribute={restProps.items[restProps.prop].attribute}
        {...restProps}
      >
        {(setIsOpen, setMaster, getMst) => (
          <Dialog
            setIsOpen={setIsOpen}
            setMaster={setMaster}
            getMaster={getMst}
            customFieldKey={customFieldKey}
          />
        )}
      </MasterSearch>
    );
  };
}

const CustomMstSearchField = customMstSearchField('customFieldValue');
const CustomMstSearch = customMstSearch('customFieldValue');

export { CustomMstSearchField, CustomMstSearch };
